<template>
<div class="chapter">
<div class="inner_page">
<div class="element element-bodymatter element-container-single element-type-chapter element-with-heading full-character-formatting" epub:type="chapter" id="vol-1-chapter-18" role="doc-chapter">
<div class="heading heading-with-title heading-without-image">
<div class="heading-contents">
<div class="title-subtitle-block title-block-title-is-element-number">
<div class="element-number-block">
</div>
<div class="title-block">
<h1 class="title case-upper">18</h1>
</div>
</div>
</div>
</div>
<div class="text" id="vol-1-chapter-18-text">
<p class="first first-in-chapter first-full-width first-with-first-letter-t"><span class="drop-cap">“T</span>ell me about him. This man. This <i>Adam Black.”</i></p>
<p class="subsq">The extent of his injuries had rendered Teacup in a comatose state for just over three weeks, but it couldn’t wait, apparently. Business as usual. He lay in a room in a private hospital in the south side of Glasgow, propped up on two pillows, every breath like broken glass scraping the innards of his chest. Several cracked ribs, a punctured lung, plus other wounds, courtesy of the man whose name had just been mentioned. But the individual at his bedside was not someone to be brushed away. Incapacity and injury had to wait in line. The individual was his relative and boss, Peter Grant, the man paying the hospital bills. The man who paid everything. The man who had lost his only son. On Teacup’s watch. He tried to mask the fear from his voice. “It’s vague. I can’t remember much. And it was dark.”</p>
<p class="subsq">Grant sat on a plastic chair, taut posture, back straight as a lance. The air was suffused with the sweet, heavy scent of his cologne. Teacup felt nauseous. On the other side of the bed, sat Nathan. Quiet, studious-looking. Teacup had always liked him. At the door, watchful and brooding, stood Grant’s bodyguard, the man called Thor.</p>
<p class="subsq">“It was dark,” echoed Grant, gazing at Teacup. He turned to Nathan. “You’re the one with the brains. So maybe you can correct me, but isn’t it always dark at night-time. Or am I being stupid.”</p>
<p class="subsq">Nathan didn’t reply.</p>
<p class="subsq">Grant focused back on Teacup. “It was dark. Everything was vague.” Grant leant forward, lowering his voice. “What type of answers are these, exactly? This is the night my son was killed in the street like an animal. Think back, Teacup. I’m sure you can do better.”</p>
<p class="subsq">Teacup swallowed, grimaced, the act causing pain. “We decided to have some drinks in Eaglesham. I thought, a quiet place. I thought…”</p>
<p class="subsq">“You thought – a quiet place, no trouble. Where my psychopathic son wouldn’t create a scene.”</p>
<p class="subsq">Teacup shook his head. The movement caused his mind to ache. “Nothing like that. We agreed a change would be… good for everybody.”</p>
<p class="subsq">Grant didn’t respond. He stared at Teacup, eyes like black stones.</p>
<p class="subsq">“We had some drinks,” Teacup continued. “We were outside, having a fag. We saw this guy running up the hill, towards us.”</p>
<p class="subsq">“Running?” said Nathan.</p>
<p class="subsq">Teacup nodded. “He was jogging. But going fast. Like he was trained.”</p>
<p class="subsq">Teacup revisited the scene in his mind. He had awakened from oblivion, and it was there, filling his brain. Every detail. He’d tried to shut it out, close the door, but it sat there, not shifting, a weight in his head.</p>
<p class="subsq">“The truth is…” he faltered.</p>
<p class="subsq">“The truth is?”</p>
<p class="subsq">Teacup took a deep breath, which hurt. “Damian had a knife. He was going to use it. He saw the guy. He wanted to have a bit of fun. He ran out, onto the road. Probably just to scare him.”</p>
<p class="subsq">“Just to scare him,” repeated Grant.</p>
<p class="subsq">“The guy Adam Black is big. And he moved quickly for a big guy. And he wasn’t scared. He reacted like he knew what he was doing.”</p>
<p class="subsq">“And you and Blakely? Where were you two when Black was killing my son.”</p>
<p class="subsq">“We were on him, I swear. At the same time. But…”</p>
<p class="subsq">“But what?”</p>
<p class="subsq">“He went through us, as if we weren’t there.”</p>
<p class="subsq">“Three of you,” said Nathan, in a quiet voice. “Against one man.”</p>
<p class="subsq">Grant’s gaze didn’t waver.</p>
<p class="subsq">“He was fast,” said Teacup. “And something else. I was on the ground. But he kept coming. Most men would have pulled back, maybe run away. But he kept coming, like…”</p>
<p class="subsq">“Like what?” said Nathan.</p>
<p class="subsq">“Like he enjoyed it.”</p>
<p class="subsq">A silence fell.</p>
<p class="subsq">Eventually Grant spoke.</p>
<p class="subsq">“So, you’re telling me this was a random event. A guy comes running up the street, out of nowhere, a fight starts, my son ends up dead. A coincidence.”</p>
<p class="subsq">“That’s all it was. We got into a fight. But with the wrong guy. Look at me. I’m lucky to be alive.”</p>
<p class="subsq">Grant stood, and regarded Teacup for five seconds. “Too fucking right.”</p>
<p class="subsq">Without a further word, he left the room, Nathan and Thor following.</p>
<p class="subsq">Teacup relaxed on the pillows, wondering how this would end. Not good, he thought dismally. For everyone.</p>
<div class="ornamental-break">
<div class="ornamental-break-block ornamental-break-aspect-wide">
<div class="ornamental-break-size-container ornamental-break-width-563">
<img alt="" class="ornamental-break-image" role="presentation" src="images/break-rule-gradient-screen.png"/>
</div>
</div>
</div>
<p class="first first-in-section first-full-width">They left the hospital, and got into a white Mercedes, parked in the visitors area.</p>
<p class="subsq">“What do you think?” Nathan asked.</p>
<p class="subsq">“About what?” Grant snapped.</p>
<p class="subsq">“Teacup’s story.”</p>
<p class="subsq">Grant paused, then said, “I believe him. His injuries speak for themselves. Seems like Black likes to dish out a bit of pain. Which is fine by me. He’s in my world now. He won’t know the meaning of the word when I get him.” He looked round to Nathan, sitting with him in the back seat, Thor driving.</p>
<p class="subsq">“But one thing I don’t buy.”</p>
<p class="subsq">Nathan waited.</p>
<p class="subsq">“I don’t believe in coincidences.”</p>
<p class="subsq">“I don’t understand.”</p>
<p class="subsq">“You will, in time. It could mean everything, or nothing.”</p>
</div>
</div>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// vol-001-chapter-018.vue?97cc4896